<template>
	<AppDialog :dialogName="dialogName" width="630">
		<div class="update-connection pt-7 px-9 pb-5 display-flex flex-column flex-justify-space-between">
			<div>
				<div class="connection-name">
					<SIcon name="successfull-connection" />
					{{ connectionService?.service_name }} Connected
					<div class="used-in" v-if="initialData">
						(Used in:
						<v-menu :close-on-content-click="false" offset-y rounded="lg">
							<template v-slot:activator="{ on, attrs }">
								<span
									v-bind="attrs"
									v-on="on"
									@click="getWorkflowsByCredentials(credentialsId)"
									:class="['flows_list', { unactive: !usedInWorkflowsCount }]"
									style="text-decoration: none; font-weight: 500"
									>{{ usedInWorkflowsText }}</span
								>
							</template>
							<v-list>
								<template v-for="(flow, index) in workflowsForUI">
									<v-list-item dense :key="`flow-${index}`" v-if="workflowsByCredentialsLoader"> Loading... </v-list-item>
									<v-divider v-if="flow.divider && !workflowsByCredentialsLoader" :key="`divider-${index}`"></v-divider>
									<v-list-item
										dense
										v-else-if="!workflowsByCredentialsLoader"
										:key="flow.workflowId"
										:to="
											flow.header
												? { name: 'Features', params: { filter: mappedFlowsCredentialsUsedIn.map((x) => x.workflowId) } }
												: { name: 'Feature', params: { wfID: flow.workflowId, pID: flow.projectId } }
										"
									>
										<v-list-item-title style="font-size: 12px" else>{{ flow.workflowTitle }}</v-list-item-title>
									</v-list-item>
								</template>
							</v-list> </v-menu
						>)
					</div>
				</div>
				<div class="title-text-cont mb-6 mt-7">{{ initialData ? 'Edit' : 'Save' }} Connection</div>

				<h3 class="reg">Connection name</h3>
				<v-text-field outlined v-model="editedConnectionName"></v-text-field>
				<h3 class="reg">Save in</h3>
				<ProjectSelectDataProvider
					v-model="selectedProjectId"
					:filterInIds="filterInIds"
					class="width-100"
					:permission="CHANGE_CREDENTIALS_PERMISSION"
				/>
				<template v-if="!isSelectedPersonalProject">
					<h5 class="personal-description">Space collaborators have access</h5>
					<div v-if="isUsedInOtherProjects" class="disabled-text mt-4">
						This option is disabled because the connection is used in more than 1 space. Stop using it in other spaces or create a new
						connection for the selected space.
					</div>
					<MaybeUpgradeRequiredIndicator :billingFeatureId="billingFeatureId" v-else-if="canEditSensitive">
						<template #default="{ indicator, isUpgradeRequired }">
							<AppCheckbox
								class="checkbox-handle"
								:isOn.sync="isProjectCredentialsSensitiveType"
								label="Sensitive connection: only connection owner and space admins can use, reconnect or delete"
								:disabled="isUpgradeRequired"
							/>
							<div class="pl-2" v-if="billingFeatureId && isUpgradeRequired">
								<component :is="indicator" :billingFeatureId="billingFeatureId" />
							</div>
						</template>
					</MaybeUpgradeRequiredIndicator>
				</template>
				<h5 v-else class="personal-description">
					Only you have access. When used in shared space, steps with this connection are locked for others, and only appear in the space
					after they are used. <a class="link" href="https://docs.ply.io" target="_blank">Learn more</a>
				</h5>
			</div>
			<div class="width-100 flex-centerize">
				<PrimaryButton :loading="isLoading" class="save-button" :disabled="saveButtonDisabled" @click="updateCredentialsAccessData">
					Save</PrimaryButton
				>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapGetters, mapActions } from 'vuex';

import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import api from '@/api';
import MaybeUpgradeRequiredIndicator from '@/components/Billing/MaybeUpgradeRequiredIndicator.vue';
import { FeatureTypes } from '@/components/Billing/billing-types';
import ProjectSelectDataProvider from '@/components/Project/ProjectSelect/ProjectSelectDataProvider.vue';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getService } from '@/store/modules/apps/getters';
import { updateUserCredentialsName, updateCredentialsData } from '@/store/modules/credentials/actions';
import { getAccountConnection } from '@/store/modules/credentials/getters';
import { projectsList, projectHasPermission, getProject } from '@/store/modules/projects/getters';
import { getWorkflowsForCredentials } from '@/store/modules/workflows/actions';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { pluralize } from '@/utils/language-service';
import ProjectService from '@/utils/projects-service';
import { PROJECTS_DEFAULT_TYPE, PROJECTS_ENTITY, PROJECTS_SENSITIVE_TYPE } from '@/utils/projects.const';
export default {
	name: 'UpdateProjectConnectionDialog',
	components: {
		AppDialog,
		ProjectSelectDataProvider,
		MaybeUpgradeRequiredIndicator,
	},
	props: {
		initialData: { type: Object },
		credentialsId: { type: Number },
		projectId: { type: Number }, // can be empty == everything tab
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGettersShorthand({ getProject, getService, projectsList }),
		...mapGetters([getAccountConnection.fullPath, projectHasPermission.fullPath, getWorkflow.fullPath, 'user']),
		billingFeatureId() {
			return FeatureTypes.SensitiveConnections;
		},
		canEditSensitive() {
			if (!this.projectId) {
				return true; // everything tab
			}
			return this[projectHasPermission.fullPath](this.projectId, ProjectPermissions.ChangeSensitiveCredentials);
		},
		saveButtonDisabled() {
			return (
				!this.credentials ||
				this.isLoading ||
				!this.selectedProjectId ||
				!this.editedConnectionName ||
				(this.isUsedInOtherProjects && !this.isSelectedPersonalProject)
			);
		},
		connectionService() {
			return this.getService(this.credentials?.service);
		},
		isSelectedPersonalProject() {
			return ProjectService.isPersonalProject(this.getProject(this.selectedProjectId));
		},
		credentials() {
			return this[getAccountConnection.fullPath](this.credentialsId);
		},
		filterInIds() {
			if (this.projectId) {
				if (ProjectService.isWorkspaceProject(this.getProject(this.projectId))) {
					const privateProjectIds = this.projectsList.filter((p) => ProjectService.isMyPersonalProject(p, this.user)).map((p) => p.id);
					return [...privateProjectIds, this.projectId];
				}
				return [this.projectId];
			}
			return null;
		},
		usedInWorkflowsCount() {
			return this.usedInWorkflows.length;
		},
		usedInWorkflowsText() {
			return pluralize('feature', this.usedInWorkflowsCount, true);
		},
		mappedFlowsCredentialsUsedIn() {
			return this.usedInWorkflows.map(({ workflow: workflowId, project: projectId }) => ({
				workflowId,
				projectId,
				workflowTitle: this[getWorkflow.fullPath](workflowId)?.workflow_title || `Module ${workflowId}`,
			}));
		},
		workflowsForUI() {
			return this.mappedFlowsCredentialsUsedIn;
		},
	},
	methods: {
		...mapActions([updateCredentialsData.fullPath, updateUserCredentialsName.fullPath, getWorkflowsForCredentials.fullPath]),
		async saveConnectionName() {
			await this[updateUserCredentialsName.fullPath]({ id: this.credentialsId, name: this.editedConnectionName });
		},
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		async updateCredentialsAccessData() {
			const currentCredentials = cloneDeep(this.credentials);
			if (this.isSelectedPersonalProject) {
				currentCredentials.access = {
					...(currentCredentials.access || {}),
					type: PROJECTS_DEFAULT_TYPE,
					related_entity_type: null,
					related_entity_id: null,
				};
			} else {
				currentCredentials.access = {
					...(currentCredentials.access || {}),
					related_entity_id: this.selectedProjectId,
					related_entity_type: PROJECTS_ENTITY,
					type: this.isProjectCredentialsSensitiveType ? PROJECTS_SENSITIVE_TYPE : PROJECTS_DEFAULT_TYPE,
				};
			}
			this.isLoading = true;
			try {
				if (this.editedConnectionName && this.editedConnectionName !== this.credentials?.connection_name) {
					await this.saveConnectionName();
				}
				await this[updateCredentialsData.fullPath]({ data: currentCredentials });
				this.$toast.success('Updated credentials successfully', { y: 'top' });
			} catch {
				this.$toast.error('Something went wrong, please try again later');
			} finally {
				this.isLoading = false;
				this.close();
			}
		},
		async getWorkflowsByCredentials(profileId) {
			this.workflowsByCredentialsLoader = true;
			await this[getWorkflowsForCredentials.fullPath]({ cid: profileId });
			this.workflowsByCredentialsLoader = false;
		},
		async getUsedInWorkflows() {
			const r = await api.getCredentialsWorkflows(this.credentialsId);
			this.usedInWorkflows = r.workflows || [];
		},
	},
	async mounted() {
		if (this.initialData) {
			if (this.initialData?.access) {
				this.isProjectCredentials = this.initialData.access.is_related;
				this.isProjectCredentialsSensitiveType = this.initialData.access.type === PROJECTS_SENSITIVE_TYPE;
			}
			await this.getUsedInWorkflows();
			// used_in_workflows is a list of workflows creds used in { workflow, project, in_current, in_draft, in_old_published }
			this.isUsedInOtherProjects =
				this.projectId && this.usedInWorkflows.filter(({ project: projectId }) => projectId != this.projectId).length > 0;
		}
		if (this.projectId) {
			this.selectedProjectId = Number(this.projectId);
		}
	},
	data: () => ({
		dialogName: 'UpdateProjectConnectionDialog',
		isProjectCredentials: true,
		isProjectCredentialsSensitiveType: false,
		selectedProjectId: null,
		isUsedInOtherProjects: false,
		localProfileData: null,
		isLoading: false,
		editedConnectionName: '',
		CHANGE_CREDENTIALS_PERMISSION: ProjectPermissions.ChangeCredentials,
		usedInWorkflows: [],
		workflowsByCredentialsLoader: false,
	}),
	watch: {
		credentials: {
			handler(newV) {
				if (newV) {
					this.editedConnectionName = newV.connection_name;
				}
			},
			immediate: true,
		},
		selectedProjectId: {
			handler() {
				this.isProjectCredentialsSensitiveType = false;
			},
		},
	},
};
</script>

<style scoped lang="scss">
.update-connection {
	height: 560px;
	.is-sensitive-container {
		::v-deep {
			.v-input--selection-controls__input {
				display: unset !important;
			}
			.primary--text {
				color: #9e9e9e !important;
				caret-color: #9e9e9e !important;
			}
		}
		.is-sensitive {
			font-size: 12px;
		}
		.disabled-text {
			font-size: 12px;
			font-weight: bold;
		}
	}
	::v-deep {
		.v-radio {
			height: 200px;
			padding-top: 84px;
		}
	}
}
.save-button {
	width: 215px !important;
	height: 45px !important;
	background: #5858ec !important;
	border-radius: 8px !important;
	font-size: 14px !important;
	letter-spacing: 0px !important;
	color: #ffffff !important;
}
.used-in {
	font-size: 15px;
	color: var(--primary-font-color);
}

.connection-name {
	font-size: 15px;
	color: var(--primary-font-color);
}
.title-text-cont {
	font-size: 22px;
	font-weight: 600;
	color: var(--primary-font-color);
}
::v-deep {
	.auto-complete-container {
		width: 100%;
	}
}
.personal-description {
	margin-top: 4px;
	opacity: 0.5;
}
.link {
	opacity: 0.5 !important;
	color: var(--primary-font-color) !important;
}

.checkbox-handle {
	::v-deep(.v-label) {
		margin: 12px 0 0 0;
		font-size: 14px;
	}
}
span.flows_list:not(.unactive):hover {
	color: #5c8df5 !important;
}
span.flows_list.unactive {
	cursor: default;
	pointer-events: none;
}
</style>
